import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import App, { preloadChunkForPath } from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter as Router } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import i18n from 'i18next'; // Updated import

import { Projects, Projects_ar } from './Projects/Main';
import { loadProjectsFromApi } from './api/projectsApi';
import animatedLogo from './assets/Animated Logo.gif';

const root = ReactDOM.createRoot(document.getElementById('root'));

// Set lang and dir based on i18n
document.documentElement.setAttribute('lang', i18n.language);
document.documentElement.setAttribute('dir', i18n.language === 'ar' ? 'rtl' : 'ltr');

// The brand intro doubles as the loading screen: the animated logo plays for
// its full length (Home used to replay it as a separate 1800ms overlay ON TOP
// of the API wait — a double block) while the projects load in parallel.
// The app-boot-spinner class is the prerender's "still loading" marker.
// Styles live in index.css (.boot-intro): the animated logo on plain white —
// deliberately NOT the blurred LoadingOverlay treatment, since there is no
// content behind it yet and this one is the brand intro.
const INTRO_MS = 1800;
const loadingScreen = (
  <div className="app-boot-spinner boot-intro">
    <img src={animatedLogo} alt="Drfah" className="boot-intro__logo" />
  </div>
);

// The projects load from the platform API before first render — the site is
// fully dynamic, the hardcoded modules are out of the bundle. A failure
// renders as one rather than as an empty site (locally: is the backend
// running, and did the dev server start after the proxy was added?).
async function bootstrap() {
  // Only when the shell is empty: a prerendered page already shows real
  // content — replacing it with the intro would be a step backwards. There
  // the static HTML stays visible until the app takes over.
  const rootEl = document.getElementById('root');
  const shellIsEmpty = rootEl.children.length === 0;
  // The app cannot hydrate the prerendered DOM (it re-renders from live data),
  // and the naked swap replayed every entry animation — a visible "second
  // load". So the prerendered content is snapshotted into a veil that stays
  // on top while the app mounts beneath, then crossfades away.
  let takeoverVeil = null;
  if (shellIsEmpty) {
    root.render(loadingScreen);
  } else {
    takeoverVeil = document.createElement('div');
    takeoverVeil.className = 'prerender-veil';
    const veilContent = document.createElement('div');
    veilContent.innerHTML = rootEl.innerHTML;
    takeoverVeil.appendChild(veilContent);
    document.body.appendChild(takeoverVeil);
    // The veil is position:fixed, but the document beneath scrolls normally —
    // sync the snapshot to the scroll position or the page reads as frozen
    // for the first seconds.
    const syncScroll = () => { veilContent.style.transform = `translateY(-${window.scrollY}px)`; };
    window.addEventListener('scroll', syncScroll, { passive: true });
    takeoverVeil.__cleanup = () => window.removeEventListener('scroll', syncScroll);
  }
  try {
    const [{ en, ar }] = await Promise.all([
      loadProjectsFromApi(),
      // The intro plays out fully even when the API is fast — a 300ms flash
      // of the logo reads as a glitch, not a brand moment.
      shellIsEmpty ? new Promise((r) => setTimeout(r, INTRO_MS)) : Promise.resolve(),
      // Warm the active route's chunk so mounting the app swaps content for
      // content — never content for a loading overlay.
      preloadChunkForPath(window.location.pathname),
    ]);
    Projects.splice(0, Projects.length, ...en);
    Projects_ar.splice(0, Projects_ar.length, ...ar);
  } catch (e) {
    console.error('[drfah] failed to load projects from the API:', e);
    root.render(
      <div style={{ padding: '4rem', fontFamily: 'monospace' }}>
        <h2>Failed to load projects from the API</h2>
        <p>{String(e)}</p>
      </div>
    );
    return;
  }
  // Home suppresses its own intro overlay when the bootstrap already handled
  // this page load's entry presentation — the boot intro on empty shells, the
  // takeover veil on prerendered pages (where an overlay would pop up OVER
  // already-visible content: the exact "second load" this file exists to
  // kill). Set at render time, not bootstrap start, so Home's freshness
  // window measures from mount rather than from before a slow API wait.
  window.__drfahBootIntroAt = Date.now();
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <Router basename="/">
          <App />
        </Router>
      </HelmetProvider>
    </React.StrictMode>
  );

  if (takeoverVeil) {
    // Two frames so the app has painted beneath, a beat for its entry
    // animations to settle, then fade the snapshot out.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setTimeout(() => {
        takeoverVeil.style.opacity = '0';
        setTimeout(() => { takeoverVeil.__cleanup?.(); takeoverVeil.remove(); }, 450);
      }, 400);
    }));
  }
}
bootstrap();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
